import { Component } from '@theme/component';

class CategorySlider extends Component {
  #index = 0;
  #resizeObserver;
  #swipeAbort;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);
    this.#setupSwipe();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#swipeAbort?.abort();
  }

  #setupSwipe() {
    const viewport = this.querySelector('.cat-slider__viewport');
    if (!viewport) return;

    viewport.style.touchAction = 'pan-y';

    this.#swipeAbort = new AbortController();
    const { signal } = this.#swipeAbort;

    let startX = 0;
    let startY = 0;
    let active = false;
    let didSwipe = false;
    const THRESHOLD = 40;

    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      startX = e.clientX;
      startY = e.clientY;
      active = true;
      didSwipe = false;
    }, { signal, passive: true });

    viewport.addEventListener('pointermove', (e) => {
      if (!active || e.pointerType !== 'touch') return;
      if (Math.abs(e.clientX - startX) > 8) didSwipe = true;
    }, { signal, passive: true });

    viewport.addEventListener('pointerup', (e) => {
      if (!active || e.pointerType !== 'touch') return;
      active = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) < THRESHOLD) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) this.next();
      else this.prev();
    }, { signal, passive: true });

    viewport.addEventListener('pointercancel', () => {
      active = false;
    }, { signal, passive: true });

    viewport.addEventListener('click', (e) => {
      if (didSwipe) {
        e.preventDefault();
        e.stopPropagation();
        didSwipe = false;
      }
    }, { signal, capture: true });
  }

  prev() {
    const slides = this.refs.slides ?? [];
    const cols = this.#getColumns();
    const max = Math.max(0, slides.length - cols);
    this.#go(this.#index <= 0 ? max : this.#index - 1);
  }

  next() {
    const slides = this.refs.slides ?? [];
    const cols = this.#getColumns();
    const max = Math.max(0, slides.length - cols);
    this.#go(this.#index >= max ? 0 : this.#index + 1);
  }

  #getColumns() {
    return parseInt(getComputedStyle(this).getPropertyValue('--cat-slider-active-columns').trim()) || 2;
  }

  #go(index) {
    this.#index = index;
    this.#update();
  }

  #update() {
    const slides = this.refs.slides ?? [];
    const cols = this.#getColumns();
    const centered = slides.length <= cols;

    if (centered) {
      this.setAttribute('data-centered', '');
      this.#index = 0;
      this.refs.track?.style.setProperty('--cat-slider-offset', '0px');
    } else {
      this.removeAttribute('data-centered');
      const slideWidth = slides[0]?.offsetWidth ?? 0;
      this.refs.track?.style.setProperty('--cat-slider-offset', `${-this.#index * slideWidth}px`);
    }

    if (this.refs.btnPrev) this.refs.btnPrev.disabled = centered;
    if (this.refs.btnNext) this.refs.btnNext.disabled = centered;
  }
}

if (!customElements.get('custom-category-slider')) {
  customElements.define('custom-category-slider', CategorySlider);
}
